import React from 'react'

const SAMPLE_RATE = 16000
const AVERAGE_LEVEL = 0.95

const WORKLET_SCRIPT = `class AP extends AudioWorkletProcessor {
                constructor(options) {
                  super(options);
                }
                process(inputs, outputs) {
                  const input = inputs[0];
                  const output = [];
                  for (let channel = 0; channel < input.length; channel += 1) {
                    output[channel] = input[channel];
                  }
                  this.port.postMessage(output[0]);
                  return true;
                }
              }
              registerProcessor('audio-processor', AP);`

/**
 * Modified version of Microsoft Cognitive Services Speech SDK Recorder
 * Source: https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common.browser/PCMRecorder.ts
 */
export const useAudioAnimation = (audioContext, audioStream) => {
  const scriptNodeRef = React.useRef(null)
  const volumeRef = React.useRef(0)

  const processAudio = React.useCallback(buffer => {
    if (!buffer) return

    const sum = buffer.reduce((acc, value) => acc + Math.pow(value, 2), 0)
    const rms = Math.sqrt(sum / buffer.length)

    volumeRef.current = Math.max(rms, volumeRef.current * AVERAGE_LEVEL)
  }, [])

  const attachScriptProcessor = React.useCallback(() => {
    const scriptNode = (() => {
      let bufferSize = 0
      try {
        return audioContext.createScriptProcessor(bufferSize, 1, 1)
      } catch (error) {
        bufferSize = 2048
        let audioSampleRate = audioContext.sampleRate
        while (bufferSize < 16384 && audioSampleRate >= 2 * SAMPLE_RATE) {
          bufferSize <<= 1
          audioSampleRate >>= 1
        }
        return audioContext.createScriptProcessor(bufferSize, 1, 1)
      }
    })()
    scriptNode.onaudioprocess = event => {
      const buffer = event.inputBuffer.getChannelData(0)
      processAudio(buffer)
    }
    audioStream.connect(scriptNode)
    scriptNode.connect(audioContext.destination)
    scriptNodeRef.current = scriptNode
  }, [audioContext, audioStream, processAudio])

  const beginAnimation = React.useCallback(() => {
    if (!audioStream || !audioContext) return

    if (audioContext.audioWorklet) {
      const blob = new Blob([WORKLET_SCRIPT], {
        type: 'application/javascript; charset=utf-8'
      })
      const processorScript = URL.createObjectURL(blob)

      audioContext.audioWorklet
        .addModule(processorScript)
        .then(() => {
          const workletNode = new AudioWorkletNode(
            audioContext,
            'audio-processor'
          )
          workletNode.port.onmessage = event => {
            const buffer = event.data
            processAudio(buffer)
          }
          audioStream.connect(workletNode)
          workletNode.connect(audioContext.destination)
          scriptNodeRef.current = workletNode
        })
        .catch(() => {
          attachScriptProcessor()
        })
    } else {
      try {
        attachScriptProcessor()
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          throw new Error(
            `Unable to start audio worklet node for AudioAnimation: ${err}`
          )
        }
      }
    }
  }, [audioContext, audioStream])

  const endAnimation = React.useCallback(() => {
    if (scriptNodeRef.current) {
      scriptNodeRef.current.disconnect(audioContext.destination)
      scriptNodeRef.current = null
    }
    volumeRef.current = 0
  }, [audioContext])

  return { beginAnimation, endAnimation, amplitude: volumeRef.current }
}
